/** score breakpoints: effective wave height (m) -> 0..10, tuned for E-Med beach fun */
const BP: [number, number][] = [
  [0, 0], [0.12, 1], [0.25, 2], [0.40, 3], [0.60, 4], [0.85, 5],
  [1.15, 6], [1.5, 7], [1.9, 8], [2.4, 9], [3.0, 10],
];

export function waveScore(h: number | null): number | null {
  if (h == null || Number.isNaN(h)) return null;
  if (h <= 0) return 0;
  for (let i = 1; i < BP.length; i++) {
    if (h <= BP[i][0]) {
      const [x0, y0] = BP[i - 1];
      const [x1, y1] = BP[i];
      return +(y0 + ((y1 - y0) * (h - x0)) / (x1 - x0)).toFixed(1);
    }
  }
  return 10;
}

export interface Band {
  /** upper bound, exclusive */
  max: number;
  label: string;
  /** background fill — white text sits on top of it (score flag, map marker) */
  fill: string;
  /** text colour for the white panel — the darker of the pair where they differ */
  ink: string;
}

/**
 * One table for the whole 0–10 scale. The two colour ramps exist because one is
 * a background and one is text on white, but they share these thresholds — as
 * separate ladders they had drifted apart (2/4/6 vs 1.5/3.5/5.5).
 */
const BANDS: Band[] = [
  { max: 1.5,      label: 'JETSKI GLASS',              fill: '#39C6C0', ink: '#1FA9A3' },
  { max: 3.5,      label: 'GENTLE',                    fill: '#2C9BD1', ink: '#2C9BD1' },
  { max: 5.5,      label: 'PLAYFUL',                   fill: '#1B7FD4', ink: '#1B7FD4' },
  { max: 7.5,      label: 'PROPER FUN ★',              fill: '#7C63D8', ink: '#7C63D8' },
  { max: 9,        label: 'HEAVY — send it carefully', fill: '#F2A93B', ink: '#D98C13' },
  { max: Infinity, label: 'STAY OUT',                  fill: '#F04E3E', ink: '#F04E3E' },
];

const NO_DATA: Band = { max: Infinity, label: 'NO DATA', fill: '#9AB0B4', ink: '#9AB0B4' };

export function band(s: number | null): Band {
  if (s == null) return NO_DATA;
  return BANDS.find((b) => s < b.max) ?? BANDS[BANDS.length - 1];
}

export const scoreColor = (s: number | null): string => band(s).fill;

export function verdict(s: number | null): [label: string, color: string] {
  const b = band(s);
  return [b.label, b.ink];
}

/** how much of the offshore wave energy actually reaches a beach facing `facing` */
export function exposure(waveDir: number | null, facing: number): number {
  if (waveDir == null) return 1;
  const d = Math.abs((((waveDir - facing) % 360) + 540) % 360 - 180); // 0 = head-on
  const c = Math.cos((d * Math.PI) / 180);
  return 0.22 + 0.78 * Math.max(0, c); // floor for refraction / wrap-around
}

const POINTS = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];

export const compass = (d: number | null): string =>
  d == null ? '–' : POINTS[Math.round(d / 22.5) % 16];
